/**
 * Landmark `sceneLinks` → storyboard chips (#173). Pure index over loaded
 * `.zwmap` docs; the board shell loads maps and looks up chips per scene.
 */

import { scenePath, type ZwChapter } from "./zwConfig";
import { mapToSvg } from "./mapToSvg";
import { displayFileName } from "./fileNames";
import {
  isJust,
  just,
  nothing,
  toMaybe,
  unwrapMaybeOr,
  type Maybe,
} from "./maybe";
import { landmarkIconById } from "./mapLandmarkIcons";
import type { MapDocument, MapLandmark } from "./mapDocument";

/** Map place linked to a manuscript scene. Rendered as a storyboard chip. */
export type StoryboardMapLocationChip = {
  id: string;
  label: string;
  kind: "map";
  mapFile: string;
  landmarkId: string;
  /** Catalog id for the landmark SVG (`castle`, `city`, …). */
  iconId: string;
  entityFile: Maybe<string>;
  previewSrc: Maybe<string>;
  locationKey: Maybe<string>;
};

export type StoryboardMapSource = {
  name: string;
  doc: MapDocument;
  assets: Maybe<ReadonlyMap<string, string>>;
};

function optionalTrimmed(
  value:
    MapLandmark["caption"] | MapLandmark["entityFile"] | MapLandmark["iconId"],
): string {
  if (typeof value !== "string") return "";
  return value.trim();
}

/** Caption, else bound entry stem, else icon label, else "Place". */
export function landmarkStoryboardLabel(landmark: MapLandmark): string {
  const caption = optionalTrimmed(landmark.caption);
  if (caption.length > 0) return caption;
  const entity = optionalTrimmed(landmark.entityFile);
  if (entity.length > 0) return displayFileName(entity);
  const iconId = optionalTrimmed(landmark.iconId);
  if (iconId.length > 0) {
    const iconM = landmarkIconById(iconId);
    if (isJust(iconM)) return iconM.value.label;
  }
  return "Place";
}

/** Index landmarks by manuscript scene path (`Chapters/<folder>/<scene>.zwdoc`). */
export function buildStoryboardMapLocationIndex(
  maps: readonly StoryboardMapSource[],
): Map<string, StoryboardMapLocationChip[]> {
  const index = new Map<string, StoryboardMapLocationChip[]>();

  for (const map of maps) {
    for (const layer of map.doc.layers) {
      for (const landmark of layer.landmarks) {
        const links = landmark.sceneLinks ?? [];
        if (links.length === 0) continue;
        const iconId = optionalTrimmed(landmark.iconId);
        const label = landmarkStoryboardLabel(landmark);
        const seenScenes = new Set<string>();
        for (const link of links) {
          const scene = optionalTrimmed(link.file);
          if (scene.length === 0 || seenScenes.has(scene)) continue;
          seenScenes.add(scene);
          const chip: StoryboardMapLocationChip = {
            id: `${map.name}:${landmark.id}:${scene}`,
            label,
            kind: "map",
            mapFile: map.name,
            landmarkId: landmark.id,
            locationKey: just(`${landmark.x}:${landmark.y}`),
            iconId,
            entityFile: toMaybe(landmark.entityFile),
            previewSrc: just(
              mapLocationPreview(
                map.doc,
                landmark,
                unwrapMaybeOr(map.assets, new Map()),
              ),
            ),
          };
          const existing = index.get(scene) ?? [];
          existing.push(chip);
          index.set(scene, existing);
        }
      }
    }
  }

  for (const map of maps) {
    for (const marker of map.doc.storyMarkers) {
      for (const scene of new Set(
        (marker.sceneLinks ?? [])
          .map((link) => link.file.trim())
          .filter((file) => file.length > 0),
      )) {
        const chips = index.get(scene) ?? [];
        chips.push({
          id: `${map.name}:marker:${marker.id}:${scene}`,
          label: displayFileName(map.name),
          kind: "map",
          mapFile: map.name,
          landmarkId: marker.id,
          locationKey: just(`${marker.x}:${marker.y}`),
          iconId: "",
          entityFile: toMaybe(marker.entityFile),
          previewSrc: just(
            mapLocationPreview(
              map.doc,
              marker,
              unwrapMaybeOr(map.assets, new Map()),
            ),
          ),
        });
        index.set(scene, chips);
      }
    }
  }

  for (const chips of index.values()) {
    chips.sort((a, b) => {
      const byLabel = a.label.localeCompare(b.label, "en", {
        sensitivity: "base",
      });
      if (byLabel !== 0) return byLabel;
      return a.id.localeCompare(b.id);
    });
  }

  return index;
}

export function storyboardMapLocationChipsForScene(
  scenePath: string,
  index: Map<string, StoryboardMapLocationChip[]>,
): StoryboardMapLocationChip[] {
  return index.get(scenePath) ?? [];
}

/** Render a fixed local area, independent of the map editor's current pan/zoom. */
export function mapLocationPreview(
  doc: MapDocument,
  point: { x: number; y: number },
  assets: ReadonlyMap<string, string>,
): string {
  const width = 600;
  const height = 360;
  const svg = mapToSvg(
    {
      ...doc,
      page: {
        width,
        height,
        centerX: point.x,
        centerY: point.y,
        frame: "none",
      },
    },
    { assetDataUrls: assets },
  );
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

/** Maps placed in the manuscript take priority; other maps retain their chip order. */
export function orderMapLocationChips(
  chips: StoryboardMapLocationChip[],
  mapFiles: string[],
): StoryboardMapLocationChip[] {
  const rank = (file: string): number => {
    const index = mapFiles.indexOf(file);
    return index < 0 ? Number.MAX_SAFE_INTEGER : index;
  };
  return [...chips].sort((a, b) => rank(a.mapFile) - rank(b.mapFile));
}

/** Suppress only consecutive repeated locations, resetting at chapter boundaries. */
export function chapterMapPreviews(
  chapters: ZwChapter[],
  index: Map<string, StoryboardMapLocationChip[]>,
  mapFiles: string[],
): Map<string, StoryboardMapLocationChip[]> {
  const previews = new Map<string, StoryboardMapLocationChip[]>();
  for (const chapter of chapters) {
    let previous: Maybe<StoryboardMapLocationChip> = nothing;
    for (const scene of chapter.scenes) {
      const path = scenePath(chapter.folder, scene.file);
      const current = toMaybe(
        orderMapLocationChips(index.get(path) ?? [], mapFiles).find((chip) =>
          isJust(chip.previewSrc),
        ),
      );
      if (isJust(current)) {
        const sameLocation =
          isJust(previous) &&
          isJust(previous.value.locationKey) &&
          isJust(current.value.locationKey) &&
          previous.value.locationKey.value === current.value.locationKey.value;
        const repeated =
          isJust(previous) &&
          previous.value.mapFile === current.value.mapFile &&
          (previous.value.landmarkId === current.value.landmarkId ||
            sameLocation);
        if (repeated === false) previews.set(path, [current.value]);
      }
      previous = current;
    }
  }
  return previews;
}
